/**
 * Chart assets for user-facing docs.
 * Add images under public/docs/charts/{id}.webp (PNG fallback supported).
 *
 * Brand mark for diagram generation: public/asset/images/cronPayBrand.png
 */
export const DOCS_CHARTS_BASE = '/docs/charts';

/** Preferred format; older PNGs in the folder still load as fallback. */
export const DOCS_CHART_EXTENSIONS = ['webp', 'png'];

export function docsChartUrl(chartId, ext = DOCS_CHART_EXTENSIONS[0]) {
    return `${DOCS_CHARTS_BASE}/${chartId}.${ext}`;
}

/** Official CronPay logo (globe + CronPay wordmark). Use in generated diagrams */
export const DOCS_BRAND_LOGO_PATH = '/asset/images/cronPayBrand.png';

export const docsChartCatalog = [
    {
        id: 'platform-overview',
        title: 'Platform overview',
        usedOn: 'Welcome',
        description:
            'High-level map of CronPay: center logo with five spokes (Direct Pay, Tracked payments, Payment requests, Company & Job workspace, and Disputes). Clean product diagram, no code or architecture layers.',
        suggestedSize: '1200 × 800',
    },
    {
        id: 'how-cronpay-works',
        title: 'How CronPay works',
        usedOn: 'What is CronPay',
        description:
            'Simple journey for non-technical users: connect wallet → choose payment or job flow → confirm in wallet → payment or escrow updates in the app. Avoid developer stack diagrams.',
        suggestedSize: '1200 × 700',
    },
    {
        id: 'payment-types-comparison',
        title: 'Payment types comparison',
        usedOn: 'Payment Types',
        description:
            'Side-by-side comparison of Direct payment, Tracked (locked) payment, Payment request, and Task escrow: when to use each, speed vs control. Table or infographic style, not bar-chart code blocks.',
        suggestedSize: '1200 × 900',
    },
    {
        id: 'onboarding-steps',
        title: 'Getting started in three steps',
        usedOn: 'Quick Start Guide',
        description:
            'Three steps with icons: (1) Connect wallet on Arc, (2) Pick Pay, Get Paid, or Jobs portal, (3) Confirm transaction in wallet. Horizontal or vertical step flow.',
        suggestedSize: '1200 × 500',
    },
    {
        id: 'tracked-milestone-timeline',
        title: 'Tracked payment milestones',
        usedOn: 'Tracked Payments',
        description:
            'Timeline: payment initiated and locked → milestone 1 unlocks → further milestones → final release. Show money unlocking over time for the receiver.',
        suggestedSize: '1200 × 600',
    },
    {
        id: 'tracked-payment-flow',
        title: 'Tracked payment lifecycle',
        usedOn: 'Tracked Payments',
        description:
            'Flow diagram: Initiate & lock → Receiver claims per milestone → Complete release, with optional Dispute branch. User-friendly labels only.',
        suggestedSize: '1200 × 700',
    },
    {
        id: 'payment-request-flow',
        title: 'Payment request flow',
        usedOn: 'Payment Requests',
        description:
            'States: Request created (pending) → Payer accepts (payment sent) or Payer declines (rejected). Show payer and receiver roles clearly.',
        suggestedSize: '1200 × 550',
    },
    {
        id: 'dispute-touchpoints',
        title: 'When disputes apply',
        usedOn: 'Disputes Overview',
        description:
            'Three-step story: a party raises a dispute → funds stay locked → admin or process resolves. Show tracked payments and job tasks as entry points.',
        suggestedSize: '1200 × 600',
    },
    {
        id: 'dispute-resolution-paths',
        title: 'Dispute resolution outcomes',
        usedOn: 'Resolution Process',
        description:
            'From “Dispute open” branch to either Release to receiver or Refund to sender, including what happens if the resolution window passes.',
        suggestedSize: '1200 × 700',
    },
    {
        id: 'task-escrow-flow',
        title: 'Task escrow flow',
        usedOn: 'Tasks & Escrow',
        description:
            'Company creates task → funds escrow → employee submits work → company approves → payout released. Jobs portal context.',
        suggestedSize: '1200 × 700',
    },
    {
        id: 'workspace-roles',
        title: 'Jobs portal roles',
        usedOn: 'Company Workspace',
        description:
            'Three role cards: Company owner (tasks, funding, approval), Employee (accept, submit, get paid), Platform admin (disputes only when needed).',
        suggestedSize: '1200 × 800',
    },
];

export function findChartById(chartId) {
    return docsChartCatalog.find((c) => c.id === chartId);
}
